// OrganizationDetailsModal.js
import React, { useState, createContext, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import { Cancel, ContentCopy, CorporateFare, Delete, People, Receipt, Settings } from '@mui/icons-material';

import OrganizationSystemFieldsTable from './OrganizationSystemFieldsTable';

import B2BAccordion from '../../b2b-common/B2BAccordion';
import B2BPopUpForm from '../../b2b-common/B2BPopUpForm';

import LicensesManagement from './LicensesManagement';
import { cloneOrganization, deleteOrganization } from '../../../services/b2b-users-apiService';

import { LoginContext } from '../../../App';

import { fetchOrganizationRoles } from '../../../services/b2b-users-apiService';
import OrgMembershipManagement from './OrgMembershipManagement';
import OrganizationMainFields from './OrganizationMainFields';

import './OrganizationDetailsModal.css';
import { FormControlLabel, Radio, RadioGroup } from '@mui/material';
import B2BDialog from '../../b2b-common/B2BDialog';

const OrganizationDetailsContext = createContext();

const OrganizationDetailsModal = ({ open, organization = {}, onClose, onUpdateOrganization, onDeleteOrganization }) => {

    const { loginData } = useContext(LoginContext);


    const [cloneDialog, setCloneDialog] = useState(false);
    const [cloneContent, setCloneContent] = useState(false);

    const [isCloning, setIsCloning] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);

    const [organizationUUID, setOrganizationUuid] = useState(null);
    const [organizationId, setorganizationId] = useState(null);
    const [organizationRoles, setOrganizationRoles] = useState([]);

    useEffect(() => {
        if (organization.organizationUUID) {
            setOrganizationUuid(organization.organizationUUID);
            setorganizationId(organization.organizationId);
            const { accessToken } = loginData;
            fetchOrganizationRoles(organization.organizationUUID, accessToken)
                .then(response => {
                    setOrganizationRoles(response);
                })
                .catch(error => {
                    console.log('error fetching roles: ', error);
                });
        }
    }, [organization])

    const handleUpdatedOrgMembership = (updatedMembers) => {
        const updatedOrganization = {
            ...organization,
            organizationMembers: updatedMembers,
        };
        onUpdateOrganization(updatedOrganization);
    };

    const handleUpdatedLicenses = (updatedLicenses) => {
        const updatedOrganization = {
            ...organization,
            organizationLicenses: updatedLicenses,
        };
        onUpdateOrganization(updatedOrganization);
    }

    const dialogTitle = <span>Organization Details</span>;
    const dialogContent = (
        <OrganizationDetailsContext.Provider value={{ organizationUUID, organizationRoles, organizationId }}>
            <B2BAccordion
                title="System Fields"
                icon={<Settings />}
                defaultExpanded={false}
                className={'b2b-accordion-org-system-fields'}
            >
                {organization && <OrganizationSystemFieldsTable organization={organization} />}
            </B2BAccordion>
            <B2BAccordion
                title="Organization Fields"
                icon={<CorporateFare />}
                defaultExpanded={true}
                className={'b2b-accordion-org-fields'}
            >
                <OrganizationMainFields
                    organization={organization}
                    onUpdateOrganization={onUpdateOrganization}
                />
            </B2BAccordion>

            <B2BAccordion
                title={`Organization Members (${organization && organization.organizationMembers ? organization.organizationMembers.length : 0})`}
                icon={<People />}
                defaultExpanded={false}
            >
                <OrgMembershipManagement
                    organization={organization}
                    onUpdatedOrgMembership={handleUpdatedOrgMembership} />
            </B2BAccordion>

            <B2BAccordion
                title={`Licenses (${organization && organization.organizationLicenses ? organization.organizationLicenses.length : 0})`}
                icon={<Receipt />}
                defaultExpanded={false}
            >
                <LicensesManagement
                    licenses={organization.organizationLicenses}
                    onUpdatedLicenses={handleUpdatedLicenses} />
            </B2BAccordion>

        </OrganizationDetailsContext.Provider>
    );

    const handleCloningOrganization = () => {
        if (!isCloning) {
            try {
                const { accessToken } = loginData;
                setIsCloning(true);
                cloneOrganization(organizationUUID, accessToken, cloneContent)
                    .then(response => {
                        console.log('cloned organization: ', response);
                        onUpdateOrganization(response);
                        onClose();
                    })
                    .catch(error => {
                        console.log('error cloning organization: ', error);
                    });
            } catch (error) {
                console.log('error cloning organization: ', error);
            } finally {
                setIsCloning(false);
                handleCloneDialogClose();
            }
        }
    };

    const handleDeleteOrganization = () => {
        try {
            const { accessToken } = loginData;
            setIsDeleting(true);
            deleteOrganization(organizationUUID, accessToken)
                .then(response => {
                    console.log('deleted organization: ', response);
                    onDeleteOrganization(organization);
                    onClose();
                })
                .catch(error => {
                    console.log('error deleting organization: ', error);
                });
        } catch (error) {
            console.log('error deleting organization: ', error);
        } finally {
            setIsDeleting(false);
        }
    };

    const handleCloneOrganization = () => {
        setCloneDialog(true);
    };

    const handleCloneContentQuestion = (event) => {
        setCloneContent(event.target.value);
    };

    const handleCloneDialogClose = () => {
        setCloneDialog(false);
    };

    return (
        <>
            <B2BPopUpForm
                open={open}
                title={dialogTitle}
                buttons={[
                    {
                        label: 'Clone',
                        isLoading: isCloning,
                        confirm: true,
                        onConfirm: handleCloneOrganization,
                        icon: <ContentCopy />
                    },
                    {
                        label: 'Delete',
                        isLoading: isDeleting,
                        confirm: true,
                        onConfirm: handleDeleteOrganization,
                        icon: <Delete />
                    },
                ]}
                onClose={onClose}
            >
                {dialogContent}
            </B2BPopUpForm>
            <B2BDialog open={cloneDialog} title='Cloning Scope'
                onClose={handleCloneDialogClose}
                buttons={[
                    {
                        label: 'Proceed',
                        onClick: handleCloningOrganization,
                        icon: <ContentCopy />
                    },
                    {
                        label: 'Cancel',
                        onClick: handleCloneDialogClose,
                        icon: <Cancel />
                    },

                ]}
            >
                <p>Do you want a total clonation of the organization?</p>
                <RadioGroup
                    aria-label="clonation-option"
                    name="clonation-option"
                    value={cloneContent}
                    onChange={handleCloneContentQuestion}
                >
                    <FormControlLabel value={true} control={<Radio />} label="Yes" />
                    <FormControlLabel value={false} control={<Radio />} label="No" />
                </RadioGroup>
            </B2BDialog>
        </>
    );
};

OrganizationDetailsModal.propTypes = {
    open: PropTypes.bool.isRequired,
    organization: PropTypes.object.isRequired,
    canEdit: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
};

export default OrganizationDetailsModal;
export { OrganizationDetailsContext };